/* eslint-disable */
import { useState, useMemo, useRef, useEffect } from "react";
import { saveToStorage, loadFromStorage } from "./Auth";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — SEASON PERFORMANCE TRACKER
   Player attribute snapshots week by week
   Pressing efficiency plotted across the full season
   Win/loss trend · Individual player development arcs
   Turns it from a weekly tool into a season-long platform
═══════════════════════════════════════════════════════════ */

const ST_CSS = `
.st-wrap { font-family:'DM Sans',sans-serif; background:var(--cream); min-height:100%; }

/* HERO */
.st-hero { background:linear-gradient(135deg,var(--teal2),var(--ink)); border-bottom:3px solid var(--mustard); padding:1.5rem 1.8rem; position:relative; overflow:hidden; }
.st-hero::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 38px,rgba(255,255,255,0.02) 38px,rgba(255,255,255,0.02) 39px); pointer-events:none; }
.st-hero-inner { position:relative; z-index:1; display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.st-hero-title { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:3px; color:var(--white); line-height:1; }
.st-hero-sub { font-size:0.72rem; color:rgba(255,255,255,0.5); font-style:italic; font-weight:300; margin-top:0.2rem; max-width:520px; line-height:1.6; }
.st-stat-strip { display:flex; gap:0; position:relative; z-index:1; margin-top:1rem; }
.st-stat { flex:1; text-align:center; padding:0.6rem 0.5rem; border-right:1px solid rgba(255,255,255,0.1); }
.st-stat:last-child { border-right:none; }
.st-stat-n { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:2px; color:var(--mustard); line-height:1; }
.st-stat-n.green { color:#4ade80; }
.st-stat-n.red { color:#f87171; }
.st-stat-l { font-size:0.52rem; font-weight:700; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.35); margin-top:0.1rem; }

/* TABS */
.st-tabs { display:flex; background:var(--white); border-bottom:2px solid var(--rule); overflow-x:auto; }
.st-tab { padding:0.85rem 1.2rem; font-family:'DM Sans',sans-serif; font-size:0.72rem; font-weight:800; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; color:var(--muted); border-bottom:3px solid transparent; margin-bottom:-2px; white-space:nowrap; transition:all 0.2s; }
.st-tab:hover { color:var(--teal); }
.st-tab.on { color:var(--teal); border-bottom-color:var(--teal); }

/* CONTENT */
.st-content { padding:1.2rem 1.5rem; }
.st-panel { background:var(--white); border:1.5px solid var(--rule); margin-bottom:1.2rem; }
.st-panel-head { padding:0.85rem 1.1rem; border-bottom:1.5px solid var(--rule); display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:0.5rem; }
.st-panel-title { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:2.5px; color:var(--ink); }
.st-panel-body { padding:1.1rem; }
.st-chip { font-family:'DM Sans',sans-serif; font-size:0.54rem; font-weight:800; padding:0.14rem 0.46rem; letter-spacing:2px; text-transform:uppercase; }
.st-chip.teal { background:var(--teal); color:var(--white); }
.st-chip.mustard { background:var(--mustard); color:var(--ink); }
.st-chip.green { background:#16a34a; color:white; }
.st-chip.red { background:var(--darkred); color:white; }

/* SEASON LINE CHART */
.st-chart-wrap { position:relative; height:200px; margin:0.5rem 0 1rem; }
.st-chart-canvas { position:absolute; inset:0; }
.st-chart-grid { position:absolute; inset:0; display:flex; flex-direction:column; justify-content:space-between; pointer-events:none; }
.st-grid-line { border-top:1px dashed var(--rule); position:relative; }
.st-grid-label { position:absolute; left:0; top:-9px; font-size:0.52rem; font-weight:700; color:var(--muted); letter-spacing:1px; }
.st-chart-legend { display:flex; gap:1rem; flex-wrap:wrap; margin-top:0.5rem; }
.st-legend-item { display:flex; align-items:center; gap:0.4rem; font-size:0.62rem; color:var(--muted); font-weight:600; }
.st-legend-line { width:20px; height:3px; border-radius:2px; }

/* MATCH TABLE */
.st-match-table { width:100%; border-collapse:collapse; }
.st-match-table th { background:var(--ink); color:rgba(255,255,255,0.55); font-family:'DM Sans',sans-serif; font-size:0.56rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; padding:0.42rem 0.75rem; text-align:left; }
.st-match-table th.r { text-align:right; }
.st-match-table th.c { text-align:center; }
.st-match-table td { padding:0.48rem 0.75rem; border-bottom:1px solid var(--rule2); font-size:0.78rem; vertical-align:middle; }
.st-match-table tr:last-child td { border-bottom:none; }
.st-match-table tr:hover td { background:var(--teal4); cursor:pointer; }
.st-result-badge { width:26px; height:26px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:0.72rem; font-weight:800; }
.st-result-badge.W { background:#16a34a; color:white; }
.st-result-badge.D { background:var(--mustard); color:var(--ink); }
.st-result-badge.L { background:var(--darkred); color:white; }
.st-score { font-family:'Bebas Neue',sans-serif; font-size:0.95rem; letter-spacing:1px; }
.st-press-val { font-family:'Bebas Neue',sans-serif; font-size:0.92rem; letter-spacing:1px; }

/* PLAYER PROGRESS */
.st-player-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:1rem; }
.st-player-card { background:var(--white); border:1.5px solid var(--rule); }
.st-player-card-head { padding:0.75rem 0.9rem; background:var(--cream2); border-bottom:1.5px solid var(--rule); display:flex; align-items:center; gap:0.65rem; }
.st-player-av { width:34px; height:34px; border-radius:50%; border:2px solid var(--ink); display:flex; align-items:center; justify-content:center; font-size:0.65rem; font-weight:800; color:white; flex-shrink:0; }
.st-player-name { font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:800; color:var(--ink); }
.st-player-meta { font-size:0.62rem; color:var(--muted); font-weight:300; margin-top:0.05rem; }
.st-player-ovr { font-family:'Bebas Neue',sans-serif; font-size:1.4rem; letter-spacing:1.5px; color:var(--teal); margin-left:auto; }
.st-player-body { padding:0.85rem 0.9rem; }

/* Attribute row with delta */
.st-attr-row { display:flex; align-items:center; gap:0.5rem; margin-bottom:0.45rem; }
.st-attr-label { font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); width:28px; flex-shrink:0; }
.st-attr-bar-bg { flex:1; height:8px; background:var(--cream2); border-radius:3px; overflow:hidden; }
.st-attr-bar-fill { height:100%; border-radius:3px; transition:width 0.8s ease; }
.st-attr-val { font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:1px; width:26px; text-align:right; flex-shrink:0; }
.st-attr-delta { font-family:'Bebas Neue',sans-serif; font-size:0.72rem; letter-spacing:1px; width:26px; text-align:center; flex-shrink:0; }
.st-attr-delta.up { color:#16a34a; }
.st-attr-delta.down { color:var(--darkred); }
.st-attr-delta.flat { color:var(--muted); }

/* Snapshot controls */
.st-snapshot-btn { padding:0.4rem 0.85rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.65rem; letter-spacing:1.5px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.st-snapshot-btn:hover { background:var(--teal2); }
.st-snapshot-date { font-size:0.62rem; color:var(--muted); font-style:italic; }

/* Edit attr input */
.st-attr-input { width:48px; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--teal); padding:0.2rem 0.4rem; font-family:'Bebas Neue',sans-serif; font-size:0.88rem; letter-spacing:1px; color:var(--ink); outline:none; text-align:center; }

/* Milestone */
.st-milestone { display:flex; align-items:center; gap:0.65rem; padding:0.55rem 0; border-bottom:1px solid var(--rule2); }
.st-milestone:last-child { border-bottom:none; }
.st-milestone-icon { font-size:1.1rem; flex-shrink:0; }
.st-milestone-text { font-size:0.78rem; font-weight:600; color:var(--ink); flex:1; }
.st-milestone-date { font-size:0.62rem; color:var(--muted); font-style:italic; flex-shrink:0; }

/* Empty */
.st-empty { text-align:center; padding:3rem 2rem; }
.st-empty-icon { font-size:3rem; margin-bottom:1rem; }
.st-empty-title { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:2px; margin-bottom:0.5rem; }
.st-empty-sub { font-size:0.8rem; color:var(--muted); font-style:italic; font-weight:300; max-width:380px; margin:0 auto; line-height:1.7; }
.st-empty-btn { margin-top:1.2rem; padding:0.72rem 1.8rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }

/* Add match form */
.st-form-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.75rem; }
.st-label { display:block; font-size:0.58rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.3rem; margin-top:0.65rem; }
.st-label:first-child { margin-top:0; }
.st-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.55rem 0.75rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; outline:none; }
.st-input:focus { border-bottom-color:var(--teal); }
.st-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.55rem 0.75rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.82rem; font-weight:500; outline:none; cursor:pointer; }
.st-btn { padding:0.68rem 1.5rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
.st-btn:hover { background:var(--teal2); }
.st-btn-s { padding:0.68rem 1.2rem; background:transparent; color:var(--muted); border:1.5px solid var(--rule); font-family:'DM Sans',sans-serif; font-weight:700; font-size:0.72rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; }

/* Pressing benchmark line */
.st-benchmark { display:flex; align-items:center; gap:0.5rem; font-size:0.62rem; color:var(--muted); font-style:italic; margin-top:0.35rem; }
.st-benchmark-line { width:20px; height:2px; border-top:2px dashed; flex-shrink:0; }

@media(max-width:900px) { .st-form-grid{grid-template-columns:1fr;} .st-player-grid{grid-template-columns:1fr;} }
@media(max-width:700px) {
  .st-content { padding:0.85rem; }
  .st-hero { padding:1.1rem; }
  .st-stat-strip { flex-wrap:wrap; }
  .st-stat { min-width:calc(33% - 2px); }
  .st-tabs { font-size:0.62rem; }
  .st-tab { padding:0.7rem 0.65rem; letter-spacing:0.5px; }
  .st-match-table th, .st-match-table td { padding:0.38rem 0.5rem; font-size:0.7rem; }
}
`;

/* ── HELPERS ── */
function posGroup(pos) {
  if(pos==="GK") return "gk";
  if(["CB","RB","LB","WB","RWB","LWB"].includes(pos)) return "def";
  if(["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}
function posGroupColor(g) {
  if(g==="gk") return "#b45309";
  if(g==="def") return "var(--darkred)";
  if(g==="mid") return "var(--teal)";
  return "#16a34a";
}
function attrColor(v) { return v>=80?"#16a34a":v>=65?"#d4941a":"#8b2a2a"; }
function ini(name) { return (name||"?").split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2); }
function fmtDate(d) { return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"2-digit"}); }
function fmtShort(d) { return new Date(d).toLocaleDateString("en-GB",{day:"numeric",month:"short"}); }

const ATTRS = ["pace","shooting","passing","dribbling","defending","physical"];
const ATTR_LABELS = {pace:"PAC",shooting:"SHO",passing:"PAS",dribbling:"DRI",defending:"DEF",physical:"PHY"};

/* ── SVG LINE CHART ── */
function LineChart({ series, height=180, yMin=0, yMax=100, benchmarks=[] }) {
  const svgRef = useRef(null);
  const [w, setW] = useState(600);

  useEffect(()=>{
    if(!svgRef.current) return;
    const obs = new ResizeObserver(e=>setW(Math.floor(e[0].contentRect.width)));
    obs.observe(svgRef.current);
    return ()=>obs.disconnect();
  },[]);

  const PAD = { top:10, right:10, bottom:30, left:32 };
  const cw = w - PAD.left - PAD.right;
  const ch = height - PAD.top - PAD.bottom;

  const toX = (i, total) => PAD.left + (total<=1 ? cw/2 : (i/(total-1))*cw);
  const toY = (v) => PAD.top + ch - ((Math.min(yMax,Math.max(yMin,v))-yMin)/(yMax-yMin))*ch;

  // Y axis labels
  const yLabels = [yMin, Math.round((yMin+yMax)/2), yMax];

  // Find all x points (union of all series dates)
  const allDates = [...new Set(series.flatMap(s=>s.data.map(d=>d.x)))].sort();

  return (
    <div ref={svgRef} style={{width:"100%",position:"relative"}}>
      <svg width={w} height={height} style={{overflow:"visible"}}>
        {/* Grid lines */}
        {yLabels.map(v=>(
          <g key={v}>
            <line x1={PAD.left} y1={toY(v)} x2={w-PAD.right} y2={toY(v)} stroke="var(--rule)" strokeWidth={1} strokeDasharray="4,3"/>
            <text x={PAD.left-4} y={toY(v)+4} textAnchor="end" fontSize={9} fill="var(--muted)" fontFamily="DM Sans">{v}</text>
          </g>
        ))}

        {/* Benchmark lines */}
        {benchmarks.map((b,i)=>(
          <g key={i}>
            <line x1={PAD.left} y1={toY(b.value)} x2={w-PAD.right} y2={toY(b.value)} stroke={b.color} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.6}/>
            <text x={w-PAD.right+4} y={toY(b.value)+4} fontSize={8} fill={b.color} fontFamily="DM Sans" opacity={0.8}>{b.label}</text>
          </g>
        ))}

        {/* X axis labels */}
        {allDates.map((d,i)=>{
          if(allDates.length>8 && i%2!==0) return null;
          return <text key={d} x={toX(i,allDates.length)} y={height-4} textAnchor="middle" fontSize={8} fill="var(--muted)" fontFamily="DM Sans">{fmtShort(d)}</text>;
        })}

        {/* Series lines and dots */}
        {series.map((s,si)=>{
          const pts = allDates.map(d=>{
            const match = s.data.find(p=>p.x===d);
            return match ? {x:allDates.indexOf(d),y:match.y} : null;
          });
          const validPts = pts.filter(Boolean);
          if(validPts.length===0) return null;

          // Build path
          const pathD = validPts.map((p,i)=>`${i===0?"M":"L"}${toX(p.x,allDates.length).toFixed(1)},${toY(p.y).toFixed(1)}`).join(" ");

          return (
            <g key={si}>
              {/* Area fill */}
              {s.fill&&validPts.length>1&&(
                <path
                  d={`${pathD} L${toX(validPts[validPts.length-1].x,allDates.length).toFixed(1)},${toY(yMin).toFixed(1)} L${toX(validPts[0].x,allDates.length).toFixed(1)},${toY(yMin).toFixed(1)} Z`}
                  fill={s.color} opacity={0.08}
                />
              )}
              {/* Line */}
              <path d={pathD} stroke={s.color} strokeWidth={2.5} fill="none" strokeLinejoin="round" strokeLinecap="round"/>
              {/* Dots */}
              {validPts.map((p,i)=>(
                <g key={i}>
                  <circle cx={toX(p.x,allDates.length)} cy={toY(p.y)} r={4} fill={s.color} stroke="white" strokeWidth={1.5}/>
                  <text x={toX(p.x,allDates.length)} y={toY(p.y)-8} textAnchor="middle" fontSize={9} fill={s.color} fontFamily="'Bebas Neue'" letterSpacing={0.5}>{p.y}</text>
                </g>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ── PLAYER DEVELOPMENT CARD ── */
function PlayerCard({ player, snapshots, editMode, onSnapshot, onAttrChange, onToggleEdit }) {
  const grp = player.group || posGroup(player.pos);

  // Get baseline (first snapshot) and current (latest snapshot or current attrs)
  const history = snapshots[player.id] || [];
  const baseline = history[0] || null;
  const current = {
    pace: player.pace||70, shooting: player.shooting||70,
    passing: player.passing||70, dribbling: player.dribbling||70,
    defending: player.defending||50, physical: player.physical||70,
    overall: player.overall||70,
  };

  // Calculate total improvement from baseline
  const totalDelta = baseline
    ? Math.round(ATTRS.reduce((s,a)=>(s+(current[a]||70)-(baseline[a]||70)),0)/ATTRS.length*10)/10
    : null;

  // Last snapshot date
  const lastSnap = history[history.length-1];

  return (
    <div className="st-player-card">
      <div className="st-player-card-head">
        <div className="st-player-av" style={{background:posGroupColor(grp)}}>{ini(player.name)}</div>
        <div style={{flex:1,minWidth:0}}>
          <div className="st-player-name" style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{player.name}</div>
          <div className="st-player-meta">
            {player.pos} · Age {player.age||"—"}
            {history.length>0&&<span style={{color:"var(--teal)",marginLeft:"0.4rem"}}>· {history.length} snapshot{history.length!==1?"s":""}</span>}
          </div>
        </div>
        <div style={{textAlign:"right",flexShrink:0}}>
          <div className="st-player-ovr">{current.overall}</div>
          {totalDelta!==null&&(
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,color:totalDelta>0?"#16a34a":totalDelta<0?"var(--darkred)":"var(--muted)"}}>
              {totalDelta>0?"+":""}{totalDelta} since start
            </div>
          )}
        </div>
      </div>
      <div className="st-player-body">
        {/* Attribute bars with deltas */}
        {ATTRS.map(attr=>{
          const val = current[attr]||70;
          const base = baseline?.[attr]||null;
          const delta = base!==null?val-base:null;
          return (
            <div key={attr} className="st-attr-row">
              <div className="st-attr-label">{ATTR_LABELS[attr]}</div>
              <div className="st-attr-bar-bg">
                <div className="st-attr-bar-fill" style={{width:`${val}%`,background:attrColor(val)}}/>
                {base!==null&&(
                  <div style={{position:"absolute",left:`${Math.min(val,base)}%`,width:`${Math.abs(delta)}%`,height:"100%",background:delta>0?"rgba(22,163,74,0.3)":"rgba(139,42,42,0.3)",top:0}}/>
                )}
              </div>
              {editMode===player.id?(
                <input className="st-attr-input" type="number" min="1" max="99" defaultValue={val}
                  onChange={e=>onAttrChange(player.id,attr,e.target.value)}/>
              ):(
                <div className="st-attr-val" style={{color:attrColor(val)}}>{val}</div>
              )}
              <div className={`st-attr-delta ${delta===null?"flat":delta>0?"up":delta<0?"down":"flat"}`}>
                {delta===null?"":delta>0?`+${delta}`:delta<0?delta:"="}
              </div>
            </div>
          );
        })}

        {/* Snapshot history */}
        {history.length>1&&(
          <div style={{marginTop:"0.75rem",paddingTop:"0.65rem",borderTop:"1px solid var(--rule2)"}}>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.56rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.4rem"}}>Snapshot History</div>
            <div style={{display:"flex",gap:"0.4rem",flexWrap:"wrap"}}>
              {history.map((snap,i)=>(
                <div key={i} style={{background:"var(--cream2)",border:"1px solid var(--rule)",padding:"0.2rem 0.45rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>
                  {fmtShort(snap.date)} — OVR {snap.overall}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{display:"flex",gap:"0.5rem",marginTop:"0.75rem",flexWrap:"wrap"}}>
          {editMode===player.id?(
            <button className="st-snapshot-btn" onClick={()=>onSnapshot(player)} style={{background:"#16a34a",borderColor:"#16a34a"}}>
              💾 Save Snapshot
            </button>
          ):(
            <button className="st-snapshot-btn" onClick={()=>onToggleEdit(player.id)}>
              📸 Update Attributes
            </button>
          )}
          {editMode===player.id&&(
            <button className="st-btn-s" style={{padding:"0.4rem 0.85rem",fontSize:"0.62rem"}} onClick={()=>onToggleEdit(null)}>Cancel</button>
          )}
          {lastSnap&&<div className="st-snapshot-date">Last updated {fmtShort(lastSnap.date)}</div>}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
export default function SeasonTracker({ squad=[], onNavigate }) {
  const [activeTab, setActiveTab] = useState("overview");

  // Load fixtures data (shared with Fixtures component)
  const fixtures = useMemo(()=>loadFromStorage("fx_fixtures",[])||[],[]);
  const played = useMemo(()=>[...fixtures.filter(f=>f.played)].sort((a,b)=>new Date(a.date)-new Date(b.date)),[fixtures]);

  // Load Management data — injury history for the season story
  const loadData = useMemo(()=>loadFromStorage("load_management",{})||{},[]);
  const injuredPlayers = useMemo(()=>{
    return squad.filter(p=>{
      const d = loadData[p.id];
      return d && d.status !== "available" && d.status !== undefined;
    }).map(p=>({
      ...p,
      loadStatus: loadData[p.id].status,
      injuryType: loadData[p.id].injuryType,
      returnDate: loadData[p.id].returnDate,
      notes: loadData[p.id].notes,
    }));
  },[squad,loadData]);

  // Player attribute snapshots — stored as array per player
  // { [playerId]: [{date, pace, shooting, ..., overall, note}] }
  const [snapshots, setSnapshots] = useState(()=>loadFromStorage("player_snapshots",{})||{});

  // Edit mode for a player card
  const [editMode, setEditMode] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Filter / search for player tracker
  const [playerSearch, setPlayerSearch] = useState("");
  const [posFilter, setPosFilter] = useState("All");
  const [sortBy, setSortBy] = useState("overall");

  // Season metrics from fixtures
  const wins   = played.filter(f=>f.goalsFor>f.goalsAgainst).length;
  const draws  = played.filter(f=>f.goalsFor===f.goalsAgainst).length;
  const losses = played.filter(f=>f.goalsFor<f.goalsAgainst).length;
  const gf     = played.reduce((s,f)=>s+(f.goalsFor||0),0);
  const ga     = played.reduce((s,f)=>s+(f.goalsAgainst||0),0);
  const pts    = wins*3+draws;

  // Pressing data across the season
  const pressData = played.filter(f=>f.pressEff>0).map(f=>({
    x: f.date,
    y: f.pressEff,
    opp: f.opponent,
    result: f.goalsFor>f.goalsAgainst?"W":f.goalsFor===f.goalsAgainst?"D":"L",
  }));

  // Possession data
  const possData = played.filter(f=>f.possession>0).map(f=>({
    x: f.date,
    y: f.possession,
  }));

  // Goals per match trend (cumulative goals for)
  const goalsData = played.map((f,i)=>({
    x: f.date,
    y: played.slice(0,i+1).reduce((s,m)=>s+(m.goalsFor||0),0),
  }));

  // Average press efficiency
  const avgPress = pressData.length>0
    ? Math.round(pressData.reduce((s,d)=>s+d.y,0)/pressData.length)
    : null;

  // Press trend — last 3 vs first 3
  const pressTrend = pressData.length>=3
    ? pressData.slice(-3).reduce((s,d)=>s+d.y,0)/3 - pressData.slice(0,3).reduce((s,d)=>s+d.y,0)/3
    : null;

  // Player milestones from snapshots
  const milestones = useMemo(()=>{
    const items = [];
    Object.entries(snapshots).forEach(([pid,snaps])=>{
      const player = squad.find(p=>String(p.id)===String(pid));
      if(!player||snaps.length<2) return;
      const first = snaps[0], last = snaps[snaps.length-1];
      const ovrDelta = last.overall - first.overall;
      if(Math.abs(ovrDelta)>=2) {
        items.push({
          icon: ovrDelta>0?"📈":"📉",
          text: `${player.name} — overall ${ovrDelta>0?"improved":"dropped"} by ${Math.abs(ovrDelta)} (${first.overall}→${last.overall})`,
          date: fmtShort(last.date),
          delta: ovrDelta,
        });
      }
      // Attribute specific milestones
      ATTRS.forEach(attr=>{
        const d = (last[attr]||70)-(first[attr]||70);
        if(d>=5) items.push({icon:"⬆️",text:`${player.name} — ${ATTR_LABELS[attr]} up ${d} points (${first[attr]||70}→${last[attr]||70})`,date:fmtShort(last.date),delta:d});
        if(d<=-5) items.push({icon:"⬇️",text:`${player.name} — ${ATTR_LABELS[attr]} down ${Math.abs(d)} points`,date:fmtShort(last.date),delta:d});
      });
    });
    return items.sort((a,b)=>Math.abs(b.delta)-Math.abs(a.delta)).slice(0,8);
  },[snapshots,squad]);

  // Snapshot handlers
  const handleAttrChange = (playerId, attr, value) => {
    setEditValues(prev=>({...prev,[`${playerId}_${attr}`]:Number(value)}));
  };

  const handleSnapshot = (player) => {
    // Build snapshot from editValues or current player attrs
    const snap = {
      date: new Date().toISOString().split("T")[0],
      overall: Math.round(ATTRS.reduce((s,a)=>{
        const v = editValues[`${player.id}_${a}`]||player[a]||70;
        return s+v;
      },0)/ATTRS.length),
    };
    ATTRS.forEach(a=>{
      snap[a] = editValues[`${player.id}_${a}`]||player[a]||70;
    });

    const updated = {
      ...snapshots,
      [player.id]: [...(snapshots[player.id]||[]), snap],
    };
    setSnapshots(updated);
    saveToStorage("player_snapshots", updated);
    setEditMode(null);
    setEditValues({});
  };

  const handleToggleEdit = (id) => {
    setEditMode(id);
    setEditValues({});
  };

  // Snapshot all players at once
  const snapshotAll = () => {
    const now = new Date().toISOString().split("T")[0];
    const updated = {...snapshots};
    squad.forEach(p=>{
      const snap = {date:now, overall:p.overall||70};
      ATTRS.forEach(a=>{ snap[a]=p[a]||70; });
      updated[p.id] = [...(updated[p.id]||[]), snap];
    });
    setSnapshots(updated);
    saveToStorage("player_snapshots", updated);
  };

  // Filtered players for dev tab
  const filteredPlayers = useMemo(()=>{
    let list = [...squad];
    if(playerSearch) list=list.filter(p=>p.name.toLowerCase().includes(playerSearch.toLowerCase())||p.pos.toLowerCase().includes(playerSearch.toLowerCase()));
    if(posFilter!=="All") list=list.filter(p=>p.pos===posFilter||p.group===posFilter);
    if(sortBy==="improvement") {
      list=list.sort((a,b)=>{
        const aSnaps=snapshots[a.id]||[], bSnaps=snapshots[b.id]||[];
        const aDelta=aSnaps.length>=2?aSnaps[aSnaps.length-1].overall-aSnaps[0].overall:0;
        const bDelta=bSnaps.length>=2?bSnaps[bSnaps.length-1].overall-bSnaps[0].overall:0;
        return bDelta-aDelta;
      });
    } else {
      list=list.sort((a,b)=>(b[sortBy]||0)-(a[sortBy]||0));
    }
    return list;
  },[squad,playerSearch,posFilter,sortBy,snapshots]);

  // How many players have 2+ snapshots (trackable)
  const trackedCount = squad.filter(p=>(snapshots[p.id]||[]).length>=2).length;

  const TABS = [
    {id:"overview",  label:"Season Overview"},
    {id:"pressing",  label:"Pressing Trend"},
    {id:"players",   label:"Player Development"},
    {id:"milestones",label:"Milestones"},
  ];

  return (
    <>
      <style>{ST_CSS}</style>
      <div className="st-wrap">

        {/* HERO */}
        <div className="st-hero">
          <div className="st-hero-inner">
            <div>
              <div className="st-hero-title">Season Performance Tracker</div>
              <div className="st-hero-sub">Track player development week by week. Plot pressing efficiency across the full season. See who's improving, who's declining, and what's working.</div>
            </div>
            <div style={{display:"flex",gap:"0.5rem",alignSelf:"flex-start"}}>
              {squad.length>0&&(
                <button onClick={snapshotAll} style={{padding:"0.45rem 0.9rem",background:"var(--mustard)",color:"var(--ink)",border:"none",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.65rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>
                  📸 Snapshot All Players
                </button>
              )}
            </div>
          </div>
          <div className="st-stat-strip">
            <div className="st-stat"><div className={`st-stat-n ${wins>losses?"green":""}`}>{played.length>0?`${wins}W ${draws}D ${losses}L`:"—"}</div><div className="st-stat-l">Season Record</div></div>
            <div className="st-stat"><div className="st-stat-n">{pts||"—"}</div><div className="st-stat-l">Points</div></div>
            <div className="st-stat"><div className="st-stat-n" style={{color:avgPress>=75?"#4ade80":avgPress>=65?"var(--mustard)":avgPress?"#f87171":"rgba(255,255,255,0.4)"}}>{avgPress?`${avgPress}%`:"—"}</div><div className="st-stat-l">Avg Pressing</div></div>
            <div className="st-stat"><div className="st-stat-n">{gf||"—"}</div><div className="st-stat-l">Goals For</div></div>
            <div className="st-stat"><div className="st-stat-n">{squad.length||"—"}</div><div className="st-stat-l">Squad Size</div></div>
            <div className="st-stat"><div className="st-stat-n">{trackedCount>0?trackedCount:"—"}</div><div className="st-stat-l">Players Tracked</div></div>
          </div>
        </div>

        {/* TABS */}
        <div className="st-tabs">
          {TABS.map(t=>(
            <div key={t.id} className={`st-tab ${activeTab===t.id?"on":""}`} onClick={()=>setActiveTab(t.id)}>{t.label}</div>
          ))}
        </div>

        <div className="st-content">

          {/* ══ SEASON OVERVIEW ══ */}
          {activeTab==="overview"&&(
            <>
              {played.length===0?(
                <div className="st-empty">
                  <div className="st-empty-icon">📅</div>
                  <div className="st-empty-title">No Match Data Yet</div>
                  <div className="st-empty-sub">Add fixtures and log your match results — the season tracker builds automatically from your data. Every result, possession figure and pressing % you log feeds into this screen.</div>
                  <button className="st-empty-btn" onClick={()=>onNavigate&&onNavigate("fixtures")}>Go to Fixtures →</button>
                </div>
              ):(
                <>
                  {/* Results trend */}
                  <div className="st-panel">
                    <div className="st-panel-head">
                      <div className="st-panel-title">Results This Season</div>
                      <span className="st-chip teal">{played.length} played</span>
                    </div>
                    <div className="st-panel-body">
                      {/* Form bar */}
                      <div style={{display:"flex",gap:4,marginBottom:"1rem",flexWrap:"wrap"}}>
                        {played.map((f,i)=>{
                          const res=f.goalsFor>f.goalsAgainst?"W":f.goalsFor===f.goalsAgainst?"D":"L";
                          return(
                            <div key={i} title={`vs ${f.opponent} — ${f.goalsFor}:${f.goalsAgainst} (${fmtShort(f.date)})`}
                              style={{width:28,height:28,borderRadius:"50%",background:res==="W"?"#16a34a":res==="D"?"var(--mustard)":"var(--darkred)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.7rem",color:res==="D"?"var(--ink)":"white",cursor:"default",flexShrink:0}}>
                              {res}
                            </div>
                          );
                        })}
                      </div>
                      {/* Stats row */}
                      <div style={{display:"grid",gridTemplateColumns:"repeat(6,1fr)",gap:"0.5rem"}}>
                        {[
                          {l:"Wins",v:wins,c:"#16a34a"},
                          {l:"Draws",v:draws,c:"var(--mustard)"},
                          {l:"Losses",v:losses,c:"var(--darkred)"},
                          {l:"Goals For",v:gf,c:"var(--teal)"},
                          {l:"Goals Against",v:ga,c:"var(--muted)"},
                          {l:"Clean Sheets",v:played.filter(f=>f.goalsAgainst===0).length,c:"#16a34a"},
                        ].map((s,i)=>(
                          <div key={i} style={{textAlign:"center",background:"var(--cream2)",padding:"0.65rem 0.4rem",border:"1px solid var(--rule2)"}}>
                            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.5rem",letterSpacing:"2px",color:s.c,lineHeight:1}}>{s.v}</div>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.56rem",fontWeight:700,letterSpacing:"1.5px",textTransform:"uppercase",color:"var(--muted)",marginTop:"0.15rem"}}>{s.l}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Full match log */}
                  <div className="st-panel">
                    <div className="st-panel-head">
                      <div className="st-panel-title">Full Match Log</div>
                      <span className="st-chip mustard">Season Record</span>
                    </div>
                    <div style={{overflowX:"auto"}}>
                      <table className="st-match-table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Opponent</th>
                            <th>Venue</th>
                            <th>Comp</th>
                            <th className="c">Result</th>
                            <th className="c">Score</th>
                            <th className="r">Possession</th>
                            <th className="r">Press %</th>
                            <th>Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {played.map((f,i)=>{
                            const res=f.goalsFor>f.goalsAgainst?"W":f.goalsFor===f.goalsAgainst?"D":"L";
                            return(
                              <tr key={i}>
                                <td style={{whiteSpace:"nowrap",color:"var(--muted)",fontSize:"0.72rem"}}>{fmtShort(f.date)}</td>
                                <td style={{fontWeight:700}}>{f.opponent}</td>
                                <td style={{fontSize:"0.72rem",color:"var(--muted)"}}>{f.venue==="home"?"Home":"Away"}</td>
                                <td><span style={{fontSize:"0.56rem",fontWeight:800,padding:"0.06rem 0.35rem",background:"var(--teal4)",color:"var(--teal)",border:"1px solid var(--teal3)",letterSpacing:"1px",textTransform:"uppercase"}}>{f.competition}</span></td>
                                <td style={{textAlign:"center"}}><div className={`st-result-badge ${res}`} style={{margin:"0 auto"}}>{res}</div></td>
                                <td style={{textAlign:"center"}}><div className="st-score">{f.goalsFor}–{f.goalsAgainst}</div></td>
                                <td style={{textAlign:"right",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.88rem",letterSpacing:"1px",color:f.possession>50?"var(--teal)":"var(--muted)"}}>{f.possession>0?`${f.possession}%`:"—"}</td>
                                <td style={{textAlign:"right"}}><div className="st-press-val" style={{color:f.pressEff>=75?"#16a34a":f.pressEff>=65?"var(--teal)":f.pressEff>=55?"var(--mustard)":f.pressEff>0?"var(--darkred)":"var(--muted)"}}>{f.pressEff>0?`${f.pressEff}%`:"—"}</div></td>
                                <td style={{fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.notes||"—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ══ PRESSING TREND ══ */}
          {activeTab==="pressing"&&(
            <>
              {pressData.length===0?(
                <div className="st-empty">
                  <div className="st-empty-icon">📈</div>
                  <div className="st-empty-title">No Pressing Data Yet</div>
                  <div className="st-empty-sub">When you log match results in Fixtures, add a pressing efficiency % for each game. Come back here to see your trend across the full season.</div>
                  <button className="st-empty-btn" onClick={()=>onNavigate&&onNavigate("fixtures")}>Log Match Results →</button>
                </div>
              ):(
                <>
                  {/* Press efficiency across season */}
                  <div className="st-panel">
                    <div className="st-panel-head">
                      <div className="st-panel-title">Pressing Efficiency — Full Season</div>
                      <div style={{display:"flex",gap:"0.5rem",alignItems:"center"}}>
                        {pressTrend!==null&&(
                          <span className={`st-chip ${pressTrend>2?"green":pressTrend<-2?"red":"mustard"}`}>
                            {pressTrend>2?"▲ Improving":pressTrend<-2?"▼ Declining":"→ Stable"}
                          </span>
                        )}
                        <span className="st-chip teal">{pressData.length} matches</span>
                      </div>
                    </div>
                    <div className="st-panel-body">
                      <LineChart
                        height={220}
                        yMin={40} yMax={100}
                        series={[{
                          data: pressData.map(d=>({x:d.x,y:d.y})),
                          color:"var(--teal)",
                          fill:true,
                        }]}
                        benchmarks={[
                          {value:75,color:"#16a34a",label:"Elite"},
                          {value:65,color:"var(--mustard)",label:"Good"},
                          {value:55,color:"var(--darkred)",label:"Concern"},
                        ]}
                      />
                      <div className="st-chart-legend" style={{marginTop:"0.75rem"}}>
                        <div className="st-benchmark"><div className="st-benchmark-line" style={{borderColor:"#16a34a"}}/> 75%+ Elite press</div>
                        <div className="st-benchmark"><div className="st-benchmark-line" style={{borderColor:"var(--mustard)"}}/> 65% Good benchmark</div>
                        <div className="st-benchmark"><div className="st-benchmark-line" style={{borderColor:"var(--darkred)"}}/> 55% Concern threshold</div>
                      </div>
                    </div>
                  </div>

                  {/* Possession trend */}
                  {possData.length>=2&&(
                    <div className="st-panel">
                      <div className="st-panel-head">
                        <div className="st-panel-title">Possession Trend</div>
                        <span className="st-chip teal">{possData.length} matches</span>
                      </div>
                      <div className="st-panel-body">
                        <LineChart
                          height={180}
                          yMin={30} yMax={75}
                          series={[{
                            data: possData.map(d=>({x:d.x,y:d.y})),
                            color:"var(--mustard)",
                            fill:true,
                          }]}
                          benchmarks={[{value:50,color:"var(--muted)",label:"50%"}]}
                        />
                      </div>
                    </div>
                  )}

                  {/* Press by result breakdown */}
                  <div className="st-panel">
                    <div className="st-panel-head">
                      <div className="st-panel-title">Pressing Efficiency by Result</div>
                    </div>
                    <div className="st-panel-body">
                      {["W","D","L"].map(res=>{
                        const matches = pressData.filter(d=>d.result===res);
                        const avg = matches.length>0?Math.round(matches.reduce((s,d)=>s+d.y,0)/matches.length):null;
                        const label = res==="W"?"Wins":res==="D"?"Draws":"Losses";
                        const color = res==="W"?"#16a34a":res==="D"?"var(--mustard)":"var(--darkred)";
                        return avg!==null?(
                          <div key={res} style={{display:"flex",alignItems:"center",gap:"0.75rem",padding:"0.55rem 0",borderBottom:"1px solid var(--rule2)"}}>
                            <div className={`st-result-badge ${res}`}>{res}</div>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",fontWeight:700,flex:1}}>{label} ({matches.length} games)</div>
                            <div style={{flex:2,height:10,background:"var(--cream2)",borderRadius:3,overflow:"hidden"}}>
                              <div style={{height:"100%",width:`${avg}%`,background:color,borderRadius:3,transition:"width 1s ease"}}/>
                            </div>
                            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"1.5px",color,width:50,textAlign:"right"}}>{avg}%</div>
                          </div>
                        ):null;
                      })}
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.75rem",lineHeight:1.6}}>
                        This shows the average pressing efficiency in each type of result — understanding if high press correlates with wins is key to developing the right system.
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ══ PLAYER DEVELOPMENT ══ */}
          {activeTab==="players"&&(
            <>
              {squad.length===0?(
                <div className="st-empty">
                  <div className="st-empty-icon">👤</div>
                  <div className="st-empty-title">No Squad Built</div>
                  <div className="st-empty-sub">Add players to your squad first. Then come back here to take attribute snapshots week by week and track each player's development across the season.</div>
                  <button className="st-empty-btn" onClick={()=>onNavigate&&onNavigate("squadcoach")}>Build Squad →</button>
                </div>
              ):(
                <>
                  {/* Controls */}
                  <div style={{display:"flex",gap:"0.75rem",marginBottom:"1rem",flexWrap:"wrap",alignItems:"center"}}>
                    <input style={{flex:1,minWidth:180,background:"var(--white)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--teal)",padding:"0.52rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",outline:"none",color:"var(--ink)"}}
                      placeholder="Search players..." value={playerSearch} onChange={e=>setPlayerSearch(e.target.value)}/>
                    <select className="st-select" style={{width:"auto"}} value={posFilter} onChange={e=>setPosFilter(e.target.value)}>
                      <option value="All">All Positions</option>
                      {["GK","CB","RB","LB","CDM","CM","CAM","LW","RW","ST"].map(p=><option key={p}>{p}</option>)}
                    </select>
                    <select className="st-select" style={{width:"auto"}} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                      <option value="overall">Sort: Overall</option>
                      <option value="improvement">Sort: Most Improved</option>
                      <option value="pace">Sort: Pace</option>
                      <option value="shooting">Sort: Shooting</option>
                      <option value="passing">Sort: Passing</option>
                    </select>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",marginLeft:"auto"}}>
                      {trackedCount}/{squad.length} players tracked · Click <strong>Update Attributes</strong> to log this week's ratings
                    </div>
                  </div>

                  {/* Instruction banner for first time */}
                  {trackedCount===0&&(
                    <div style={{background:"var(--teal4)",border:"1.5px solid var(--teal)",borderLeft:"4px solid var(--teal)",padding:"0.85rem 1rem",marginBottom:"1rem",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:"0.75rem"}}>
                      <div>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.9rem",letterSpacing:"2px",color:"var(--teal)",marginBottom:"0.2rem"}}>How Player Tracking Works</div>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",color:"var(--ink2)",fontWeight:300,lineHeight:1.6}}>
                          Click <strong>Update Attributes</strong> on any player to adjust their current ratings. Click <strong>Save Snapshot</strong> to record that point in time. Do this weekly and you'll see their development arc build up across the season. Or click <strong>Snapshot All Players</strong> at the top to record all current attributes at once.
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Player cards */}
                  <div className="st-player-grid">
                    {filteredPlayers.map(p=>(
                      <PlayerCard
                        key={p.id}
                        player={p}
                        snapshots={snapshots}
                        editMode={editMode}
                        onSnapshot={handleSnapshot}
                        onAttrChange={handleAttrChange}
                        onToggleEdit={handleToggleEdit}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ══ MILESTONES ══ */}
          {activeTab==="milestones"&&(
            <>
              {milestones.length===0?(
                <div className="st-empty">
                  <div className="st-empty-icon">🏆</div>
                  <div className="st-empty-title">No Milestones Yet</div>
                  <div className="st-empty-sub">Milestones appear automatically when players show significant improvement or decline across multiple snapshots. Take your first player snapshots to start building the story of your season.</div>
                </div>
              ):(
                <div className="st-panel">
                  <div className="st-panel-head">
                    <div className="st-panel-title">Player Milestones</div>
                    <span className="st-chip teal">{milestones.length} recorded</span>
                  </div>
                  <div className="st-panel-body">
                    {milestones.map((m,i)=>(
                      <div key={i} className="st-milestone">
                        <div className="st-milestone-icon">{m.icon}</div>
                        <div className="st-milestone-text">{m.text}</div>
                        <div className="st-milestone-date">{m.date}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Season story */}
              {(played.length>0||injuredPlayers.length>0)&&(
                <div className="st-panel">
                  <div className="st-panel-head">
                    <div className="st-panel-title">Season Story</div>
                  </div>
                  <div className="st-panel-body">
                    {[
                      played.length>0&&{icon:"⚽",text:`${played.length} matches played — ${wins} wins, ${draws} draws, ${losses} losses`,date:"Season"},
                      avgPress&&{icon:"📊",text:`Average pressing efficiency: ${avgPress}% — ${avgPress>=75?"elite level":avgPress>=65?"good standard":"needs work"}`,date:"Season avg"},
                      gf>0&&{icon:"🥅",text:`${gf} goals scored, ${ga} conceded — ${gf>ga?"positive":"negative"} goal difference of ${Math.abs(gf-ga)}`,date:"Season"},
                      trackedCount>0&&{icon:"📈",text:`${trackedCount} player${trackedCount!==1?"s":""} tracked with attribute snapshots`,date:"Development"},
                    ].filter(Boolean).map((item,i)=>(
                      <div key={i} className="st-milestone">
                        <div className="st-milestone-icon">{item.icon}</div>
                        <div className="st-milestone-text">{item.text}</div>
                        <div className="st-milestone-date">{item.date}</div>
                      </div>
                    ))}

                    {/* Injury / Load Management */}
                    {injuredPlayers.length>0&&(
                      <>
                        <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.8rem",letterSpacing:"2px",color:"var(--darkred)",marginTop:"0.85rem",marginBottom:"0.35rem",borderTop:"1px solid var(--rule2)",paddingTop:"0.7rem"}}>
                          Current Availability Issues
                        </div>
                        {injuredPlayers.map((p,i)=>{
                          const daysLeft = p.returnDate ? Math.ceil((new Date(p.returnDate)-new Date())/(1000*60*60*24)) : null;
                          const statusLabel = p.loadStatus==="injured"?"Injured":p.loadStatus==="suspended"?"Suspended":p.loadStatus==="recovery"?"In Recovery":"Carrying Knock";
                          const icon = p.loadStatus==="injured"?"🚑":p.loadStatus==="suspended"?"🟣":"⚠️";
                          return(
                            <div key={i} className="st-milestone">
                              <div className="st-milestone-icon">{icon}</div>
                              <div className="st-milestone-text">
                                {p.name} — {statusLabel}{p.injuryType?` (${p.injuryType})`:""}
                                {daysLeft!==null&&daysLeft>=0&&<span style={{color:"var(--muted)",fontWeight:400}}> · back in {daysLeft}d</span>}
                                {daysLeft!==null&&daysLeft<0&&<span style={{color:"var(--teal)",fontWeight:400}}> · return date passed</span>}
                              </div>
                              <div className="st-milestone-date">{p.returnDate?fmtShort(p.returnDate):"No ETA"}</div>
                            </div>
                          );
                        })}
                      </>
                    )}
                    {injuredPlayers.length===0&&squad.length>0&&(
                      <div className="st-milestone">
                        <div className="st-milestone-icon">✅</div>
                        <div className="st-milestone-text">Full squad availability — no current injury concerns</div>
                        <div className="st-milestone-date">Now</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

        </div>
      </div>
    </>
  );
}
